import { OutboxEntry, OutboxStatus, SyncedCreateRow } from 'src/app/core/offline/offline.models';
import {
  CommentView,
  SnapshotComment,
  SnapshotMember,
  SnapshotTask,
  TaskCommentPayload,
  TaskCreatePayload,
  TaskPhotoPayload,
  TaskUpdatePayload,
  TaskView,
} from './task-offline.models';

// Offline E2 step 3: pure functions behind TaskOfflineService. Local tasks and changes live only in the outbox and are
// laid over server or snapshot data (plan C1); nothing here writes.

export interface TaskOverlayContext {
  userId: string;
  /** snapshot members per project, for the local «zugewiesen» hint (Q2) */
  membersByProject: Map<string, SnapshotMember[]>;
  /** project names of the snapshots: header of a local task (FE-Review H5) */
  projectNames?: Map<string, string | null>;
}

const PROBLEMS: ReadonlySet<OutboxStatus> = new Set(['conflict', 'failed', 'blocked']);

const FLAGS = {
  localId: null,
  pending: false,
  uploading: false,
  sent: false,
  problem: null,
  notSynced: false,
  offlinePictureCount: 0,
} as const;

/** a server DTO (GET /tasks, GET /tasks/:id): all its fields stay, the offline flags are added */
export function toTaskView(server: Record<string, unknown>): TaskView {
  return {
    pictures: [],
    taskStatusLogs: [],
    newCommentsCount: 0,
    version: 1,
    ...server,
    ...FLAGS,
    localPhotos: [],
  } as unknown as TaskView;
}

/** projectName: the details and gallery headers show it (the snapshot task has none) */
export function fromSnapshotTask(task: SnapshotTask, projectId: string, projectName: string | null = null): TaskView {
  const { pictureCount, ...fields } = task;
  return {
    ...fields,
    // E2 gate R4: a truncated note arrives as null; the views and filters work with text
    note: fields.note ?? '',
    projectId,
    projectName,
    newCommentsCount: 0,
    pictures: [],
    taskStatusLogs: [],
    ...FLAGS,
    offlinePictureCount: pictureCount,
    localPhotos: [],
  };
}

/** Q2: only internal members count; guest rows (guest true, id null) never, and rows are never keyed by id */
export function assignedLocally(bkpNumber: string | null | undefined, members: SnapshotMember[]): boolean {
  if (!bkpNumber) return false;
  return members.some((member) => member.guest !== true && member.type !== 'sharedLink' && member.bkps.includes(bkpNumber));
}

/** the task an entry belongs to: its server id once known, else the local id */
export function taskKeyOf(entry: OutboxEntry, ids: Map<string, string>): string | null {
  const { localId, serverId } = entry.entityRef;
  if (localId) return ids.get(localId) ?? localId;
  return serverId;
}

function localTaskView(entry: Pick<OutboxEntry, 'payload' | 'entityRef' | 'createdAt'>, ctx: TaskOverlayContext): TaskView {
  const payload = entry.payload as unknown as TaskCreatePayload;
  const localId = entry.entityRef.localId!;
  return {
    id: localId,
    projectId: entry.entityRef.projectId,
    projectName: ctx.projectNames?.get(entry.entityRef.projectId) ?? null,
    totalCommentsCount: 0,
    journalId: payload.journalId,
    journalName: payload.journalName ?? null,
    journalType: payload.journalType ?? null,
    bkp: payload.fields.bkp,
    location: payload.fields.location,
    note: payload.fields.note,
    status: payload.fields.status,
    deadlineDate: payload.fields.deadlineDate ?? null,
    completionDate: payload.fields.completionDate ?? null,
    creationDate: payload.fields.creationDate ?? new Date(entry.createdAt).toISOString(),
    userId: ctx.userId,
    isNew: false,
    hasAssignedMember: assignedLocally(payload.fields.bkp?.number, ctx.membersByProject.get(entry.entityRef.projectId) ?? []),
    version: 1,
    newCommentsCount: 0,
    pictures: [],
    taskStatusLogs: [],
    ...FLAGS,
    localId,
    notSynced: true,
    localPhotos: [],
  };
}

function flag(view: TaskView, entry: OutboxEntry): void {
  view.pending = true;
  if (entry.status === 'running') view.uploading = true;
  if (!view.problem && PROBLEMS.has(entry.status)) view.problem = entry.status;
}

/**
 * Server or snapshot tasks with the outbox on top: local creates as tasks, updates applied, queued deletes hidden,
 * photos as local photos. projectId null: entries of all projects (/tasks without project). synced: creates already on
 * the server that the snapshot does not show yet (NA-03); only for device data, the server list never needs them.
 */
export function overlayTasks(
  base: TaskView[],
  entries: OutboxEntry[],
  ids: Map<string, string>,
  ctx: TaskOverlayContext,
  projectId: string | null,
  synced: SyncedCreateRow[] = [],
): TaskView[] {
  const views = new Map(base.map((view) => [view.id, { ...view, localPhotos: [...view.localPhotos] } as TaskView]));
  for (const row of synced) {
    if (row.type !== 'task.create' || (projectId !== null && row.projectId !== projectId) || views.has(row.serverId)) continue;
    const view = localTaskView({ payload: row.payload, entityRef: { projectId: row.projectId, localId: row.localId, serverId: row.serverId }, createdAt: row.createdAt }, ctx);
    // a server task now: its id, no «wartet auf Upload», opens like any task of the snapshot
    views.set(row.serverId, { ...view, id: row.serverId, localId: null, notSynced: false });
  }
  const relevant = entries.filter(
    (entry) => entry.type.startsWith('task.') && (projectId === null || entry.entityRef.projectId === projectId),
  );
  const deleted = new Set<string>();

  for (const entry of relevant) {
    if (entry.type !== 'task.create' || !entry.entityRef.localId) continue;
    const serverId = ids.get(entry.entityRef.localId);
    // the server list has it already (mapped, entry about to go): no second card
    if (serverId && views.has(serverId)) continue;
    views.set(entry.entityRef.localId, localTaskView(entry, ctx));
  }

  for (const entry of relevant) {
    const key = taskKeyOf(entry, ids);
    const view = key ? (views.get(key) ?? (entry.entityRef.localId ? views.get(entry.entityRef.localId) : undefined)) : undefined;
    if (!view) continue;
    flag(view, entry);
    switch (entry.type) {
      case 'task.create':
        view.sent = view.sent || !!entry.sent;
        break;
      case 'task.update':
        Object.assign(view, (entry.payload as unknown as TaskUpdatePayload).fields);
        // a note of the user is on the device now (E2 gate R4)
        if (view.noteTruncated && (entry.payload as unknown as TaskUpdatePayload).fields.note !== undefined) view.noteTruncated = false;
        break;
      case 'task.delete':
        // a delete in conflict (E-3: the task changed meanwhile) keeps the newer task visible until it is resolved
        if (entry.status !== 'conflict') deleted.add(view.id);
        break;
      case 'task.comment':
        // the comment counter of header and card includes comments still waiting on this device (FE-Review H5)
        view['totalCommentsCount'] = Number(view['totalCommentsCount'] ?? 0) + 1;
        break;
      case 'task.photo': {
        const payload = entry.payload as unknown as TaskPhotoPayload;
        view.localPhotos.push({
          opId: entry.opId,
          blobId: payload.blobIds?.[0] ?? '',
          fileName: payload.fileName,
          status: entry.status,
          sent: !!entry.sent,
        });
        break;
      }
      default:
        break;
    }
  }
  return [...views.values()].filter((view) => !deleted.has(view.id));
}

/** a server comment (GET /tasks/:id/comments): its fields stay */
export function toCommentView(server: Record<string, unknown>): CommentView {
  return {
    userId: null,
    user: { firstName: null, lastName: null, organization: null },
    isNew: false,
    ...server,
    truncated: false,
    local: false,
    pending: false,
    problem: null,
  } as unknown as CommentView;
}

export function fromSnapshotComment(comment: SnapshotComment, userId: string): CommentView {
  return {
    id: comment.id,
    taskId: comment.taskId,
    text: comment.text,
    creationDate: comment.creationDate,
    userId: comment.isOwn ? userId : null,
    // one name field: the templates print firstName + lastName
    user: { firstName: comment.authorName, lastName: null, organization: { name: comment.companyName } },
    isNew: false,
    truncated: !!comment.truncated,
    local: false,
    pending: false,
    problem: null,
  };
}

/** comments of one task, newest first, with its outbox comments on top (author «Ich») */
export function overlayComments(
  taskId: string,
  base: CommentView[],
  entries: OutboxEntry[],
  ids: Map<string, string>,
  me: { userId: string; name: string; organization?: string | null },
): CommentView[] {
  const target = ids.get(taskId) ?? taskId;
  const local = entries
    .filter((entry) => entry.type === 'task.comment' && taskKeyOf(entry, ids) === target)
    .map((entry): CommentView => {
      const payload = entry.payload as unknown as TaskCommentPayload;
      return {
        id: entry.opId,
        taskId: target,
        text: payload.text,
        creationDate: new Date(entry.createdAt).toISOString(),
        userId: me.userId,
        // my company from the snapshot members, else unknown (the page shows its default, NA-02)
        user: { firstName: me.name, lastName: null, organization: me.organization ? { name: me.organization } : null },
        isNew: false,
        truncated: false,
        local: true,
        pending: true,
        problem: PROBLEMS.has(entry.status) ? entry.status : null,
      };
    })
    .reverse();
  return [...local, ...base];
}
